'use client';

import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { QRCodeSVG } from 'qrcode.react';
import { BUSINESS_CATEGORIES, type BusinessCategory, type NoShowPolicy } from '@slikerr/shared';
import { trpc } from '@/lib/trpc';
import { useAuth } from '@/lib/auth-context';
import { Button, Card, Input } from '@/components/ds';
import { OwnerAddressField } from '@/components/onboarding/OwnerAddressField';
import { publicBookingUrl } from '@/lib/public-booking-url';
import {
  CANCELLATION_WINDOW_OPTIONS,
  LATE_ARRIVAL_GRACE_OPTIONS,
  MIN_BOOKING_NOTICE_OPTIONS,
  NO_SHOW_POLICY_OPTIONS,
  type PolicyOption,
} from '@/lib/policy-options';

/** Roles that can edit the business profile. */
const EDIT_ROLES = ['manager', 'business_owner'] as const;

/**
 * Client-side mirrors of `profileUpdateSchema` (apps/api/src/trpc/
 * business-profile.ts). Kept as literals rather than imported because the
 * schema lives in the API workspace, which the web app never imports from;
 * if the server ever widens them, widen these in the same push or the form
 * refuses a value the server would have accepted.
 */
const PHONE_MAX = 32;
const DESCRIPTION_MAX = 2000;
const BOOKING_POLICY_MAX = 2000;

/** Two trade lists are equal when they hold the same values, order aside. */
function sameTrades(a: readonly string[], b: readonly string[]): boolean {
  if (a.length !== b.length) return false;
  const sortedA = [...a].sort();
  const sortedB = [...b].sort();
  return sortedA.every((v, i) => v === sortedB[i]);
}

/** Chip styling for the trade pickers — inline, like the rest of this page. */
function chipStyle(active: boolean, disabled: boolean): React.CSSProperties {
  return {
    fontFamily: 'var(--font-sans)',
    fontSize: 'var(--fs-body)',
    padding: '0.375rem 0.875rem',
    borderRadius: 'var(--r-full)',
    border: `1px solid ${active ? 'var(--brand)' : 'var(--border-subtle)'}`,
    background: active ? 'var(--brand)' : 'var(--bg-surface)',
    color: active ? 'var(--on-brand)' : 'var(--text-primary)',
    cursor: disabled ? 'default' : 'pointer',
    opacity: disabled ? 0.6 : 1,
  };
}

/**
 * One booking-policy question (#459) — a native `<select>`, matching the rest
 * of the dashboard (services, staff, calendar) rather than the trade chips:
 * these labels are whole sentences, and eight of them as chips in a 520px
 * column is unreadable.
 *
 * ZERO IS AN ANSWER, not an absence — "flexible", "no grace period" and "no
 * minimum" are all `0`. So the option is never resolved with `Number(...)`
 * (`Number('')` is `0`, and `Number(v) || null` turns a real `0` into null).
 * The picked `<option>` is looked up in the SAME array the list was rendered
 * from, by its string form, so `0` can only ever come back as `0` and the
 * empty string can only ever come back as null.
 */
function PolicySelect<T extends number | string>(props: {
  label: string;
  value: T | null;
  options: PolicyOption<T>[];
  /**
   * Whether the list offers a "not set" choice. The three optional policies
   * do. The cancellation window does NOT: it is publish gate 2, and
   * `applyProfileUpdate` only checks that gate when a call sets
   * `isPublished: true` — so clearing it on an already-published business
   * would succeed and leave a live business with no cancellation policy.
   * A null value still SHOWS as "not set" (an unpublished business has one);
   * it just cannot be chosen again.
   */
  clearable: boolean;
  notSetLabel: string;
  disabled: boolean;
  testId: string;
  onChange: (next: T | null) => void;
}) {
  const { t } = useTranslation();
  const { label, value, options, clearable, notSetLabel, disabled, testId, onChange } = props;

  return (
    <label
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 'var(--sp-3)',
        fontFamily: 'var(--font-sans)',
        marginBlockEnd: '1.25rem',
      }}
    >
      <span
        style={{
          fontSize: 'var(--fs-label)',
          fontWeight: 'var(--fw-semibold)' as React.CSSProperties['fontWeight'],
          color: 'var(--text-primary)',
        }}
      >
        {label}
      </span>
      <select
        value={value === null ? '' : String(value)}
        disabled={disabled}
        data-testid={testId}
        onChange={(e) => {
          const raw = e.target.value;
          if (raw === '') {
            onChange(null);
            return;
          }
          const picked = options.find((o) => String(o.value) === raw);
          onChange(picked ? picked.value : null);
        }}
        style={{
          fontFamily: 'var(--font-sans)',
          fontSize: 'var(--fs-body)',
          color: 'var(--text-primary)',
          background: disabled ? 'var(--bg-sunken)' : 'var(--bg-surface)',
          border: '1px solid var(--border-strong)',
          borderRadius: 'var(--r-sm)',
          padding: 'var(--sp-3) var(--sp-5)',
        }}
      >
        {/* Rendered while the stored value is null even when the list is not
            clearable, so the control never shows the first option as though
            the owner had picked it. */}
        {(clearable || value === null) && (
          <option value="" disabled={!clearable}>
            {notSetLabel}
          </option>
        )}
        {options.map((o) => (
          <option key={String(o.value)} value={String(o.value)}>
            {t(o.labelKey)}
          </option>
        ))}
      </select>
    </label>
  );
}

export default function SettingsPage() {
  const { t } = useTranslation();
  const auth = useAuth();

  const canEdit = EDIT_ROLES.includes(
    (auth.activeBusiness?.role ?? '') as (typeof EDIT_ROLES)[number],
  );

  const profileQuery = trpc.business.getProfile.useQuery(undefined, {
    // Don't retry aggressively on error — surface it fast.
    retry: 1,
  });

  /**
   * #459 — the four structured booking policies plus the free-text note are
   * writable through `business.updateProfile` but are NOT on `profileOutput`,
   * so `getProfile` cannot read them back. `ownerBusinessPage` is the
   * owner-scoped read that carries them (the same one mobile's
   * `business-advanced-settings` uses), hence the second query rather than a
   * widened API contract.
   */
  const pageQuery = trpc.business.ownerBusinessPage.useQuery(undefined, { retry: 1 });

  const utils = trpc.useUtils();
  const updateMutation = trpc.business.updateProfile.useMutation({
    onSuccess: () => {
      void utils.business.getProfile.invalidate();
      // The policy fields are read from ownerBusinessPage, so invalidating
      // getProfile alone would leave the form showing the values it had
      // BEFORE the save. Deliberately not added to the publish/overlap
      // mutations below — see the re-seeding note on the effect.
      void utils.business.ownerBusinessPage.invalidate();
      setSuccess(true);
    },
  });

  // Separate mutation state for the publish toggle so its pending/error
  // does not bleed into the profile form's UI.
  const publishMutation = trpc.business.updateProfile.useMutation({
    onSuccess: () => {
      void utils.business.getProfile.invalidate();
      setPublishSuccess(true);
    },
  });

  // T30 — the "confirm overlap anyway" opt-in, same isolated-mutation-state
  // pattern as the publish toggle above.
  const overlapMutation = trpc.business.setOverlapConfirmEnabled.useMutation({
    onSuccess: () => {
      void utils.business.getProfile.invalidate();
      setOverlapSuccess(true);
    },
  });

  // Controlled form state — initialised from the query once it resolves.
  const [name, setName] = useState('');
  const [instagramUrl, setInstagramUrl] = useState('');
  const [success, setSuccess] = useState(false);
  const [nameError, setNameError] = useState<string | null>(null);

  // ── #95 marketplace profile fields ──────────────────────────────────────
  const [phone, setPhone] = useState('');
  const [phoneError, setPhoneError] = useState<string | null>(null);
  const [address, setAddress] = useState('');
  /**
   * A FRESH Places pick, never seeded from the stored lat/lng. Same rule
   * mobile's contact sheet uses (`business-profile-settings.tsx`) and the
   * onboarding wizard's `hasAddressPin`: typed-but-never-picked text is not an
   * address, so `address`/`lat`/`lng` go only when a pick backs the text.
   * Seeding this from the profile would let hand-edited text overwrite a good
   * address and leave the old pin attached to it.
   *
   * `city` rides in this same state rather than beside it, deliberately: it is
   * derived from the SAME Places pick, so keeping them in one value makes it
   * impossible to send a city that belongs to a different address. Issue #458
   * happened because `city` was simply never carried at all.
   */
  const [coords, setCoords] = useState<{
    lat: number;
    lng: number;
    city: string | null;
  } | null>(null);
  const [addressTouched, setAddressTouched] = useState(false);
  const [category, setCategory] = useState<BusinessCategory | null>(null);
  const [secondaryCategories, setSecondaryCategories] = useState<BusinessCategory[]>([]);
  /**
   * The wizard's own "more than one trade" flag. It is the FLAG, not the
   * length of the list, that decides what gets saved — un-ticking hides the
   * picks without discarding them, so a mis-tap is recoverable.
   */
  const [hasMultipleTrades, setHasMultipleTrades] = useState(false);
  const [description, setDescription] = useState('');
  const [descriptionError, setDescriptionError] = useState<string | null>(null);

  // ── #459 booking policy fields (read from ownerBusinessPage) ────────────
  // `null` means "not chosen"; `0` is a real answer in three of the four
  // lists, so nothing here may test these for truthiness.
  const [cancellationWindow, setCancellationWindow] = useState<number | null>(null);
  const [lateArrivalGrace, setLateArrivalGrace] = useState<number | null>(null);
  const [noShowPolicy, setNoShowPolicy] = useState<NoShowPolicy | null>(null);
  const [minBookingNotice, setMinBookingNotice] = useState<number | null>(null);
  const [bookingPolicy, setBookingPolicy] = useState('');
  const [bookingPolicyError, setBookingPolicyError] = useState<string | null>(null);

  // Publish toggle success state — separate from the profile form.
  const [publishSuccess, setPublishSuccess] = useState(false);
  // Overlap-confirm toggle success state — separate from the profile form.
  const [overlapSuccess, setOverlapSuccess] = useState(false);

  // Share-link card — the public origin is only known client-side; resolving
  // it in an effect (not inline during render) avoids an SSR/client hydration
  // mismatch, since the server has no notion of "this request's origin" here.
  const [origin, setOrigin] = useState<string | null>(null);
  useEffect(() => {
    setOrigin(window.location.origin);
  }, []);
  const [linkCopied, setLinkCopied] = useState(false);

  // Sync form fields when query data arrives or changes.
  // NOTE: do NOT call setSuccess(false) here — the invalidate() refetch that
  // follows a successful save would immediately erase the success banner.
  //
  // KNOWN LIMITATION (pre-existing, widened by #95): this re-seeds on EVERY new
  // `profileQuery.data`, including the refetch the publish and overlap toggles
  // trigger. Toggling either mid-edit discards whatever is typed but unsaved in
  // this form. It used to cost two fields; it now costs eight (and #459's own
  // effect below adds five more, though only an ownerBusinessPage invalidate
  // re-seeds those, which is why the two toggles do not trigger one). Fixing it means
  // seeding once (or diffing against a dirty flag) — deliberately out of scope
  // here rather than changed silently.
  useEffect(() => {
    if (profileQuery.data) {
      const d = profileQuery.data;
      setName(d.name);
      setInstagramUrl(d.instagramUrl ?? '');
      setPhone(d.phone ?? '');
      setAddress(d.address ?? '');
      setCoords(null);
      setAddressTouched(false);
      setCategory((d.category as BusinessCategory | null) ?? null);
      const secondary = (d.secondaryCategories ?? []) as BusinessCategory[];
      setSecondaryCategories(secondary);
      setHasMultipleTrades(secondary.length > 0);
      setDescription(d.description ?? '');
    }
  }, [profileQuery.data]);

  /**
   * #459 — the policy fields seed from their OWN query, in their own effect.
   * Folding them into the effect above would seed them from a `pageQuery` that
   * has not resolved yet on first paint, writing nulls the later render would
   * then have to fight. Same re-seeding limitation as the effect above: this
   * runs on every new `pageQuery.data`, so an unsaved policy edit is lost if
   * something else invalidates that query mid-edit.
   *
   * `?? null` and `?? ''`, never `||` — `0` must survive as `0`.
   */
  useEffect(() => {
    if (pageQuery.data) {
      const p = pageQuery.data;
      setCancellationWindow(p.cancellationWindowMinutes ?? null);
      setLateArrivalGrace(p.lateArrivalGraceMinutes ?? null);
      setNoShowPolicy(p.noShowPolicy ?? null);
      setMinBookingNotice(p.minBookingNoticeMinutes ?? null);
      setBookingPolicy(p.bookingPolicy ?? '');
    }
  }, [pageQuery.data]);

  /** Picking a main trade must never leave it ALSO listed as a secondary —
   *  the two lists mean "the main one" and "everything else", and
   *  `applyProfileUpdate` enforces the same rule server-side. */
  const chooseCategory = (next: BusinessCategory) => {
    setCategory(next);
    setSecondaryCategories((prev) => prev.filter((c) => c !== next));
  };

  const toggleSecondary = (c: BusinessCategory) =>
    setSecondaryCategories((prev) =>
      prev.includes(c) ? prev.filter((x) => x !== c) : [...prev, c],
    );

  const addressUnpinned = addressTouched && coords === null && address.trim() !== '';

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setSuccess(false);
    setNameError(null);
    setPhoneError(null);
    setDescriptionError(null);
    setBookingPolicyError(null);

    if (!name.trim()) {
      setNameError(t('settings.name_required'));
      return;
    }
    // Mirrors the API's own `z.string().max(...)` bounds so the form refuses
    // what the server would refuse, with a field-level message instead of a
    // raw tRPC error.
    if (phone.trim().length > PHONE_MAX) {
      setPhoneError(t('settings.phone_too_long'));
      return;
    }
    if (description.trim().length > DESCRIPTION_MAX) {
      setDescriptionError(t('settings.description_too_long'));
      return;
    }
    if (bookingPolicy.trim().length > BOOKING_POLICY_MAX) {
      setBookingPolicyError(t('settings.policy_notes_too_long'));
      return;
    }

    const loaded = profileQuery.data;
    // Extras are gated on the FLAG, exactly as mobile's identity sheet gates
    // them: extras that are hidden must not be persisted.
    const effectiveSecondary = hasMultipleTrades
      ? secondaryCategories.filter((c) => c !== category)
      : [];
    const nextInstagram = instagramUrl.trim() === '' ? null : instagramUrl.trim();
    const nextPhone = phone.trim() === '' ? null : phone.trim();
    const nextDescription = description.trim() === '' ? null : description.trim();

    // `name` always goes, every other field only when it actually changed:
    // an unchanged field left out of the patch cannot clobber a value another
    // surface wrote, and the always-present `name` is what keeps the patch
    // from being empty (the API refuses `{}`).
    const patch: Parameters<typeof updateMutation.mutate>[0] = { name: name.trim() };
    if (nextInstagram !== (loaded?.instagramUrl ?? null)) patch.instagramUrl = nextInstagram;
    if (nextPhone !== (loaded?.phone ?? null)) patch.phone = nextPhone;
    if (category !== ((loaded?.category as BusinessCategory | null) ?? null)) {
      patch.category = category;
    }
    if (!sameTrades(effectiveSecondary, loaded?.secondaryCategories ?? [])) {
      patch.secondaryCategories = effectiveSecondary;
    }
    if (nextDescription !== (loaded?.description ?? null)) patch.description = nextDescription;
    // address + lat + lng + city travel as ONE bundle and only when a fresh
    // Places pick backs the text. Without a pick all four are omitted, so the
    // stored address and its pin survive untouched rather than being clobbered
    // with free-typed text (issue #400's rule; mobile's contact sheet does the
    // same). `city` is sent even when null — the server reads an omitted city
    // on a changed address as "stale, clear it", which is the same outcome, but
    // sending it keeps this bundle honest about what the pick actually found.
    if (coords) {
      patch.address = address.trim() === '' ? null : address.trim();
      patch.lat = coords.lat;
      patch.lng = coords.lng;
      patch.city = coords.city;
    }

    // #459 booking policies — same "only what changed" rule, diffed against
    // their own query. Skipped entirely while `pageQuery` is still loading:
    // the state is all nulls until it resolves, and sending those would
    // wipe a policy the owner never touched.
    //
    // `!==` against the loaded value, never a truthiness test: `0` is a real
    // answer, so `0` vs `null` IS a change and must be sent.
    const policies = pageQuery.data;
    if (policies) {
      const nextBookingPolicy = bookingPolicy.trim() === '' ? null : bookingPolicy.trim();
      if (cancellationWindow !== (policies.cancellationWindowMinutes ?? null)) {
        patch.cancellationWindowMinutes = cancellationWindow;
      }
      if (lateArrivalGrace !== (policies.lateArrivalGraceMinutes ?? null)) {
        patch.lateArrivalGraceMinutes = lateArrivalGrace;
      }
      if (noShowPolicy !== (policies.noShowPolicy ?? null)) {
        patch.noShowPolicy = noShowPolicy;
      }
      if (minBookingNotice !== (policies.minBookingNoticeMinutes ?? null)) {
        patch.minBookingNoticeMinutes = minBookingNotice;
      }
      if (nextBookingPolicy !== (policies.bookingPolicy ?? null)) {
        patch.bookingPolicy = nextBookingPolicy;
      }
    }

    updateMutation.mutate(patch);
  };

  const handlePublishToggle = () => {
    if (!profileQuery.data) return;
    setPublishSuccess(false);
    publishMutation.mutate({ isPublished: !profileQuery.data.isPublished });
  };

  const handleOverlapToggle = () => {
    if (!profileQuery.data) return;
    setOverlapSuccess(false);
    overlapMutation.mutate({ enabled: !profileQuery.data.allowOverlappingBookings });
  };

  const isPublished = profileQuery.data?.isPublished ?? true;
  // undefined while loading -> treat as approved so the banner never flashes
  // on a normal load. The server gate is the real enforcement either way.
  const isPendingReview = profileQuery.data ? profileQuery.data.approvedAt === null : false;
  const overlapEnabled = profileQuery.data?.allowOverlappingBookings ?? false;
  const bookingUrl =
    origin && profileQuery.data ? publicBookingUrl(origin, profileQuery.data.slug) : null;

  const handleCopyLink = () => {
    if (!bookingUrl) return;
    void navigator.clipboard.writeText(bookingUrl).then(() => {
      setLinkCopied(true);
      setTimeout(() => setLinkCopied(false), 2000);
    });
  };

  return (
    <div style={{ maxInlineSize: '520px', marginInline: 'auto', padding: '2rem 1.5rem' }}>
      <h1
        style={{
          fontSize: 'var(--fs-title1)',
          fontWeight: 'var(--fw-semibold)' as React.CSSProperties['fontWeight'],
          fontFamily: 'var(--font-sans)',
          color: 'var(--text-primary)',
          marginBlockEnd: '1.5rem',
        }}
      >
        {t('settings.title')}
      </h1>

      {profileQuery.isPending && (
        <p style={{ color: 'var(--text-secondary)', fontFamily: 'var(--font-sans)' }}>
          {t('common.loading')}
        </p>
      )}

      {profileQuery.error && (
        <p role="alert" style={{ color: 'var(--error)', fontFamily: 'var(--font-sans)' }}>
          {t('common.error')}: {profileQuery.error.message}
        </p>
      )}

      {profileQuery.data && (
        <>
          {!canEdit && (
            <p
              role="status"
              style={{
                background: 'var(--warn-wash)',
                border: '1px solid var(--warn)',
                borderRadius: 'var(--r-sm)',
                padding: '0.5rem 0.75rem',
                marginBlockEnd: '1rem',
                fontSize: 'var(--fs-body)',
                fontFamily: 'var(--font-sans)',
                color: 'var(--warn)',
              }}
            >
              {t('settings.read_only_notice')}
            </p>
          )}

          {/* ── T25 Publish gate toggle ────────────────────────────────── */}
          <Card style={{ marginBlockEnd: '1.75rem' }}>
            <div aria-labelledby="visibility-heading">
              <p
                id="visibility-heading"
                style={{
                  fontWeight: 'var(--fw-semibold)' as React.CSSProperties['fontWeight'],
                  fontFamily: 'var(--font-sans)',
                  color: 'var(--text-primary)',
                  marginBlockEnd: 'var(--sp-3)',
                  fontSize: 'var(--fs-body-l)',
                }}
              >
                {t('settings.visibility_label')}
              </p>
              <p
                style={{
                  fontSize: 'var(--fs-body)',
                  fontFamily: 'var(--font-sans)',
                  marginBlockEnd: isPendingReview ? 'var(--sp-2)' : 'var(--sp-4)',
                  color: isPendingReview
                    ? 'var(--text-primary)'
                    : isPublished
                      ? 'var(--success)'
                      : 'var(--error)',
                }}
              >
                {isPendingReview
                  ? t('settings.pending_review_status')
                  : isPublished
                    ? t('settings.published_status')
                    : t('settings.unpublished_status')}
              </p>

              {isPendingReview && (
                <p
                  style={{
                    fontSize: 'var(--fs-caption)',
                    fontFamily: 'var(--font-sans)',
                    marginBlockEnd: 'var(--sp-4)',
                    color: 'var(--text-secondary)',
                  }}
                  data-testid="pending-review-banner"
                >
                  {t('settings.pending_review_body')}
                </p>
              )}

              {publishMutation.error && (
                <p
                  role="alert"
                  style={{
                    color: 'var(--error)',
                    fontFamily: 'var(--font-sans)',
                    fontSize: 'var(--fs-body)',
                    marginBlockEnd: 'var(--sp-3)',
                  }}
                >
                  {publishMutation.error.message}
                </p>
              )}

              {publishSuccess && (
                <p
                  role="status"
                  style={{
                    color: 'var(--success)',
                    fontFamily: 'var(--font-sans)',
                    fontSize: 'var(--fs-body)',
                    marginBlockEnd: 'var(--sp-3)',
                  }}
                >
                  {t('settings.publish_success')}
                </p>
              )}

              {canEdit && (
                <Button
                  type="button"
                  variant={isPublished ? 'danger' : 'primary'}
                  size="sm"
                  disabled={publishMutation.isPending || (isPendingReview && !isPublished)}
                  onClick={handlePublishToggle}
                  aria-label={
                    isPublished ? t('settings.unpublish_button') : t('settings.publish_button')
                  }
                  data-testid="publish-toggle"
                >
                  {publishMutation.isPending
                    ? t('settings.publish_saving')
                    : isPublished
                      ? t('settings.unpublish_button')
                      : t('settings.publish_button')}
                </Button>
              )}
            </div>
          </Card>
          {/* ─────────────────────────────────────────────────────────── */}

          {/* ── T30 Overlap-confirm opt-in ─────────────────────────────── */}
          <Card style={{ marginBlockEnd: '1.75rem' }}>
            <div aria-labelledby="overlap-heading">
              <p
                id="overlap-heading"
                style={{
                  fontWeight: 'var(--fw-semibold)' as React.CSSProperties['fontWeight'],
                  fontFamily: 'var(--font-sans)',
                  color: 'var(--text-primary)',
                  marginBlockEnd: 'var(--sp-3)',
                  fontSize: 'var(--fs-body-l)',
                }}
              >
                {t('settings.overlap_label')}
              </p>
              <p
                style={{
                  fontSize: 'var(--fs-body)',
                  fontFamily: 'var(--font-sans)',
                  marginBlockEnd: 'var(--sp-3)',
                  color: 'var(--text-secondary)',
                }}
              >
                {t('settings.overlap_description')}
              </p>
              <p
                style={{
                  fontSize: 'var(--fs-body)',
                  fontFamily: 'var(--font-sans)',
                  marginBlockEnd: 'var(--sp-4)',
                  color: overlapEnabled ? 'var(--success)' : 'var(--text-secondary)',
                }}
              >
                {overlapEnabled
                  ? t('settings.overlap_enabled_status')
                  : t('settings.overlap_disabled_status')}
              </p>

              {overlapMutation.error && (
                <p
                  role="alert"
                  style={{
                    color: 'var(--error)',
                    fontFamily: 'var(--font-sans)',
                    fontSize: 'var(--fs-body)',
                    marginBlockEnd: 'var(--sp-3)',
                  }}
                >
                  {overlapMutation.error.message}
                </p>
              )}

              {overlapSuccess && (
                <p
                  role="status"
                  style={{
                    color: 'var(--success)',
                    fontFamily: 'var(--font-sans)',
                    fontSize: 'var(--fs-body)',
                    marginBlockEnd: 'var(--sp-3)',
                  }}
                >
                  {t('settings.overlap_success')}
                </p>
              )}

              {canEdit && (
                <Button
                  type="button"
                  variant={overlapEnabled ? 'danger' : 'primary'}
                  size="sm"
                  disabled={overlapMutation.isPending}
                  onClick={handleOverlapToggle}
                  aria-label={
                    overlapEnabled
                      ? t('settings.overlap_disable_button')
                      : t('settings.overlap_enable_button')
                  }
                  data-testid="overlap-confirm-toggle"
                >
                  {overlapMutation.isPending
                    ? t('settings.overlap_saving')
                    : overlapEnabled
                      ? t('settings.overlap_disable_button')
                      : t('settings.overlap_enable_button')}
                </Button>
              )}
            </div>
          </Card>
          {/* ─────────────────────────────────────────────────────────── */}

          {/* ── Shareable public booking link ──────────────────────────── */}
          <Card style={{ marginBlockEnd: '1.75rem' }}>
            <div aria-labelledby="share-link-heading">
              <p
                id="share-link-heading"
                style={{
                  fontWeight: 'var(--fw-semibold)' as React.CSSProperties['fontWeight'],
                  fontFamily: 'var(--font-sans)',
                  color: 'var(--text-primary)',
                  marginBlockEnd: 'var(--sp-3)',
                  fontSize: 'var(--fs-body-l)',
                }}
              >
                {t('settings.share_link_label')}
              </p>

              {!isPublished && (
                <p
                  role="status"
                  data-testid="share-link-unpublished-notice"
                  style={{
                    fontSize: 'var(--fs-body)',
                    fontFamily: 'var(--font-sans)',
                    marginBlockEnd: 'var(--sp-4)',
                    color: 'var(--text-secondary)',
                  }}
                >
                  {t('settings.share_link_unpublished_notice')}
                </p>
              )}

              {bookingUrl && (
                <div
                  style={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: 'var(--sp-4)',
                    alignItems: 'flex-start',
                  }}
                >
                  <div style={{ flex: '1 1 260px', minWidth: 0 }}>
                    {/* Dimmed only while the link is dead. It used to be the
                        whole block, which now holds the preview link — the one
                        control that DOES work before publishing, and the last
                        thing that should look disabled. */}
                    <Input
                      style={{ opacity: isPublished ? 1 : 0.5 }}
                      value={bookingUrl}
                      readOnly
                      latin
                      aria-label={t('settings.share_link_label')}
                      data-testid="share-link-url"
                    />
                    <div
                      style={{
                        display: 'flex',
                        gap: 'var(--sp-3)',
                        marginBlockStart: 'var(--sp-3)',
                        alignItems: 'center',
                      }}
                    >
                      <Button
                        type="button"
                        variant="secondary"
                        size="sm"
                        disabled={!isPublished}
                        onClick={handleCopyLink}
                        data-testid="share-link-copy"
                      >
                        {linkCopied
                          ? t('settings.share_link_copied')
                          : t('settings.share_link_copy_button')}
                      </Button>
                      {/* Published -> the real public page. Unpublished -> the
                          owner preview, which renders the SAME page component
                          without the publish gate. Hiding the link entirely
                          (what this did before) was safe but left the owner
                          with no way to see their own page during exactly the
                          stretch when they are deciding whether to publish it. */}
                      {isPublished ? (
                        <a
                          href={bookingUrl}
                          target="_blank"
                          rel="noreferrer"
                          data-testid="share-link-view-live"
                          style={{
                            fontFamily: 'var(--font-sans)',
                            fontSize: 'var(--fs-body)',
                            color: 'var(--brand)',
                          }}
                        >
                          {t('settings.share_link_view_live')}
                        </a>
                      ) : (
                        <a
                          href="/dashboard/preview"
                          target="_blank"
                          rel="noreferrer"
                          data-testid="share-link-preview"
                          style={{
                            fontFamily: 'var(--font-sans)',
                            fontSize: 'var(--fs-body)',
                            color: 'var(--brand)',
                          }}
                        >
                          {t('settings.share_link_preview')}
                        </a>
                      )}
                    </div>
                  </div>
                  {isPublished && (
                    <div data-testid="share-link-qr">
                      <QRCodeSVG value={bookingUrl} size={120} />
                    </div>
                  )}
                </div>
              )}
            </div>
          </Card>
          {/* ─────────────────────────────────────────────────────────── */}

          <Card>
            <form onSubmit={handleSubmit} aria-label={t('settings.title')}>
              {/* Business name */}
              <div style={{ marginBlockEnd: '1.25rem' }}>
                <Input
                  label={t('settings.name_label')}
                  value={name}
                  onChange={(e) => {
                    setName(e.target.value);
                    if (nameError) setNameError(null);
                  }}
                  disabled={!canEdit || updateMutation.isPending}
                  readOnly={!canEdit}
                  aria-describedby={nameError ? 'settings-name-error' : undefined}
                />
                {nameError && (
                  <p
                    id="settings-name-error"
                    role="alert"
                    style={{
                      color: 'var(--error)',
                      fontFamily: 'var(--font-sans)',
                      fontSize: 'var(--fs-caption)',
                      marginBlockStart: 'var(--sp-2)',
                    }}
                  >
                    {nameError}
                  </p>
                )}
              </div>

              {/* ── #95 Marketplace profile ──────────────────────────────
                  What a customer sees about this business, in the order the
                  onboarding wizard asks for it. Same semantics as mobile's
                  business-profile-settings.tsx sheets, field for field. */}
              <p
                style={{
                  fontWeight: 'var(--fw-semibold)' as React.CSSProperties['fontWeight'],
                  fontFamily: 'var(--font-sans)',
                  fontSize: 'var(--fs-body-l)',
                  color: 'var(--text-primary)',
                  marginBlockEnd: 'var(--sp-4)',
                }}
              >
                {t('settings.marketplace_heading')}
              </p>

              {/* Business phone — free text, exactly as the API stores it
                  (`z.string().max(32)`); deliberately NOT normalized here, so
                  web and mobile save the same string for the same input. */}
              <div style={{ marginBlockEnd: '1.25rem' }}>
                <Input
                  label={t('settings.phone_label')}
                  value={phone}
                  onChange={(e) => {
                    setPhone(e.target.value);
                    if (phoneError) setPhoneError(null);
                  }}
                  disabled={!canEdit || updateMutation.isPending}
                  readOnly={!canEdit}
                  placeholder={t('settings.phone_placeholder')}
                  latin
                  data-testid="settings-phone"
                  aria-describedby={phoneError ? 'settings-phone-error' : undefined}
                />
                {phoneError && (
                  <p
                    id="settings-phone-error"
                    role="alert"
                    style={{
                      color: 'var(--error)',
                      fontFamily: 'var(--font-sans)',
                      fontSize: 'var(--fs-caption)',
                      marginBlockStart: 'var(--sp-2)',
                    }}
                  >
                    {phoneError}
                  </p>
                )}
              </div>

              {/* Address — a pick, not typed text. See `coords` above. */}
              <div style={{ marginBlockEnd: '1.25rem' }}>
                <OwnerAddressField
                  label={t('settings.address_label')}
                  placeholder={t('settings.address_placeholder')}
                  value={address}
                  disabled={!canEdit || updateMutation.isPending}
                  // Typing CLEARS the pin: the address and its coordinates are
                  // sent as one bundle, so a stale pin on edited text would put
                  // the business on the map at its previous address.
                  onChangeText={(text) => {
                    setAddressTouched(true);
                    setAddress(text);
                    setCoords(null);
                  }}
                  onSelect={({ address: picked, lat, lng, city }) => {
                    setAddress(picked);
                    setCoords({ lat, lng, city });
                  }}
                />
                {addressUnpinned && (
                  <p
                    role="status"
                    data-testid="settings-address-unpinned"
                    style={{
                      color: 'var(--warn)',
                      fontFamily: 'var(--font-sans)',
                      fontSize: 'var(--fs-caption)',
                      marginBlockStart: 'var(--sp-2)',
                    }}
                  >
                    {t('settings.address_unpinned')}
                  </p>
                )}
              </div>

              {/* Main trade */}
              <fieldset
                style={{ border: 'none', padding: 0, margin: 0, marginBlockEnd: '1.25rem' }}
              >
                <legend
                  style={{
                    fontFamily: 'var(--font-sans)',
                    fontSize: 'var(--fs-label)',
                    fontWeight: 'var(--fw-semibold)' as React.CSSProperties['fontWeight'],
                    color: 'var(--text-primary)',
                    padding: 0,
                    marginBlockEnd: 'var(--sp-3)',
                  }}
                >
                  {t('settings.trade_label')}
                </legend>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--sp-2)' }}>
                  {BUSINESS_CATEGORIES.map((c) => (
                    <button
                      key={c}
                      type="button"
                      aria-pressed={category === c}
                      disabled={!canEdit || updateMutation.isPending}
                      onClick={() => chooseCategory(c)}
                      style={chipStyle(category === c, !canEdit || updateMutation.isPending)}
                      data-testid={`settings-trade-${c}`}
                    >
                      {t(`vertical.${c}`)}
                    </button>
                  ))}
                </div>
              </fieldset>

              {/* Extra trades — gated on the flag, not the list's length. */}
              <label
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 'var(--sp-3)',
                  fontFamily: 'var(--font-sans)',
                  fontSize: 'var(--fs-body)',
                  color: 'var(--text-primary)',
                  marginBlockEnd: 'var(--sp-3)',
                }}
              >
                <input
                  type="checkbox"
                  checked={hasMultipleTrades}
                  disabled={!canEdit || updateMutation.isPending}
                  onChange={(e) => setHasMultipleTrades(e.target.checked)}
                  data-testid="settings-multi-trade"
                />
                <span>{t('settings.multi_trade_label')}</span>
              </label>

              {hasMultipleTrades && (
                <div
                  style={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: 'var(--sp-2)',
                    marginBlockEnd: '1.25rem',
                  }}
                  aria-label={t('settings.secondary_trade_label')}
                  data-testid="settings-secondary-trades"
                >
                  {BUSINESS_CATEGORIES.filter((c) => c !== category).map((c) => (
                    <button
                      key={c}
                      type="button"
                      aria-pressed={secondaryCategories.includes(c)}
                      disabled={!canEdit || updateMutation.isPending}
                      onClick={() => toggleSecondary(c)}
                      style={chipStyle(
                        secondaryCategories.includes(c),
                        !canEdit || updateMutation.isPending,
                      )}
                      data-testid={`settings-secondary-${c}`}
                    >
                      {t(`vertical.${c}`)}
                    </button>
                  ))}
                </div>
              )}

              {/* About the business — an empty box CLEARS the blurb rather
                  than storing "", same as mobile's identity sheet. */}
              <div style={{ marginBlockEnd: '1.5rem' }}>
                <label
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 'var(--sp-3)',
                    fontFamily: 'var(--font-sans)',
                  }}
                >
                  <span
                    style={{
                      fontSize: 'var(--fs-label)',
                      fontWeight: 'var(--fw-semibold)' as React.CSSProperties['fontWeight'],
                      color: 'var(--text-primary)',
                    }}
                  >
                    {t('settings.description_label')}
                  </span>
                  <textarea
                    value={description}
                    rows={4}
                    disabled={!canEdit || updateMutation.isPending}
                    readOnly={!canEdit}
                    placeholder={t('settings.description_placeholder')}
                    onChange={(e) => {
                      setDescription(e.target.value);
                      if (descriptionError) setDescriptionError(null);
                    }}
                    data-testid="settings-description"
                    aria-describedby={descriptionError ? 'settings-description-error' : undefined}
                    style={{
                      fontFamily: 'var(--font-sans)',
                      fontSize: 'var(--fs-body)',
                      color: 'var(--text-primary)',
                      background: !canEdit ? 'var(--bg-sunken)' : 'var(--bg-surface)',
                      border: `1px solid ${
                        descriptionError ? 'var(--error)' : 'var(--border-strong)'
                      }`,
                      borderRadius: 'var(--r-sm)',
                      padding: 'var(--sp-3) var(--sp-5)',
                      resize: 'vertical',
                    }}
                  />
                </label>
                <p
                  style={{
                    fontFamily: 'var(--font-sans)',
                    fontSize: 'var(--fs-caption)',
                    color: 'var(--text-secondary)',
                    marginBlockStart: 'var(--sp-2)',
                  }}
                  data-testid="settings-description-counter"
                >
                  {t('settings.description_counter', { count: description.trim().length })}
                </p>
                {descriptionError && (
                  <p
                    id="settings-description-error"
                    role="alert"
                    style={{
                      color: 'var(--error)',
                      fontFamily: 'var(--font-sans)',
                      fontSize: 'var(--fs-caption)',
                      marginBlockStart: 'var(--sp-2)',
                    }}
                  >
                    {descriptionError}
                  </p>
                )}
              </div>
              {/* ───────────────────────────────────────────────────────── */}

              {/* Instagram URL */}
              <div style={{ marginBlockEnd: '1.5rem' }}>
                <Input
                  label={t('settings.instagram_label')}
                  value={instagramUrl}
                  onChange={(e) => setInstagramUrl(e.target.value)}
                  disabled={!canEdit || updateMutation.isPending}
                  readOnly={!canEdit}
                  placeholder={t('settings.instagram_placeholder')}
                  latin
                />
              </div>

              {/* ── #459 Booking policy ──────────────────────────────────
                  The five policy fields `business.updateProfile` accepts and
                  no owner-facing web control used to set. Same questions, same
                  answers and the same Hebrew wording as mobile's
                  business-advanced-settings (labels in `lib/policy-options`).
                  Rendered only once `ownerBusinessPage` has answered — the
                  values live on that query, not on `getProfile`. */}
              {pageQuery.data && (
                <>
                  <p
                    style={{
                      fontWeight: 'var(--fw-semibold)' as React.CSSProperties['fontWeight'],
                      fontFamily: 'var(--font-sans)',
                      fontSize: 'var(--fs-body-l)',
                      color: 'var(--text-primary)',
                      marginBlockEnd: 'var(--sp-4)',
                    }}
                  >
                    {t('settings.policy_heading')}
                  </p>

                  <PolicySelect
                    label={t('settings.policy_cancellation_label')}
                    value={cancellationWindow}
                    options={CANCELLATION_WINDOW_OPTIONS}
                    // NOT clearable — publish gate 2. See PolicySelect's own note.
                    clearable={false}
                    notSetLabel={t('settings.policy_not_set')}
                    disabled={!canEdit || updateMutation.isPending}
                    testId="settings-cancellation-window"
                    onChange={setCancellationWindow}
                  />
                  {cancellationWindow === null && (
                    <p
                      role="status"
                      data-testid="settings-cancellation-required"
                      style={{
                        color: 'var(--warn)',
                        fontFamily: 'var(--font-sans)',
                        fontSize: 'var(--fs-caption)',
                        marginBlockStart: '-0.75rem',
                        marginBlockEnd: '1.25rem',
                      }}
                    >
                      {t('settings.policy_cancellation_required_hint')}
                    </p>
                  )}

                  <PolicySelect
                    label={t('settings.policy_late_arrival_label')}
                    value={lateArrivalGrace}
                    options={LATE_ARRIVAL_GRACE_OPTIONS}
                    clearable
                    notSetLabel={t('settings.policy_not_set')}
                    disabled={!canEdit || updateMutation.isPending}
                    testId="settings-late-arrival-grace"
                    onChange={setLateArrivalGrace}
                  />

                  <PolicySelect
                    label={t('settings.policy_no_show_label')}
                    value={noShowPolicy}
                    options={NO_SHOW_POLICY_OPTIONS}
                    clearable
                    notSetLabel={t('settings.policy_not_set')}
                    disabled={!canEdit || updateMutation.isPending}
                    testId="settings-no-show-policy"
                    onChange={setNoShowPolicy}
                  />

                  <PolicySelect
                    label={t('settings.policy_min_notice_label')}
                    value={minBookingNotice}
                    options={MIN_BOOKING_NOTICE_OPTIONS}
                    clearable
                    notSetLabel={t('settings.policy_not_set')}
                    disabled={!canEdit || updateMutation.isPending}
                    testId="settings-min-booking-notice"
                    onChange={setMinBookingNotice}
                  />

                  {/* Free-text note — an empty box CLEARS it, same as the
                      description above and mobile's own policy sheet. */}
                  <div style={{ marginBlockEnd: '1.5rem' }}>
                    <label
                      style={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 'var(--sp-3)',
                        fontFamily: 'var(--font-sans)',
                      }}
                    >
                      <span
                        style={{
                          fontSize: 'var(--fs-label)',
                          fontWeight: 'var(--fw-semibold)' as React.CSSProperties['fontWeight'],
                          color: 'var(--text-primary)',
                        }}
                      >
                        {t('settings.policy_notes_label')}
                      </span>
                      <textarea
                        value={bookingPolicy}
                        rows={4}
                        disabled={!canEdit || updateMutation.isPending}
                        readOnly={!canEdit}
                        placeholder={t('settings.policy_notes_placeholder')}
                        onChange={(e) => {
                          setBookingPolicy(e.target.value);
                          if (bookingPolicyError) setBookingPolicyError(null);
                        }}
                        data-testid="settings-booking-policy"
                        aria-describedby={
                          bookingPolicyError ? 'settings-booking-policy-error' : undefined
                        }
                        style={{
                          fontFamily: 'var(--font-sans)',
                          fontSize: 'var(--fs-body)',
                          color: 'var(--text-primary)',
                          background: !canEdit ? 'var(--bg-sunken)' : 'var(--bg-surface)',
                          border: `1px solid ${
                            bookingPolicyError ? 'var(--error)' : 'var(--border-strong)'
                          }`,
                          borderRadius: 'var(--r-sm)',
                          padding: 'var(--sp-3) var(--sp-5)',
                          resize: 'vertical',
                        }}
                      />
                    </label>
                    <p
                      style={{
                        fontFamily: 'var(--font-sans)',
                        fontSize: 'var(--fs-caption)',
                        color: 'var(--text-secondary)',
                        marginBlockStart: 'var(--sp-2)',
                      }}
                      data-testid="settings-booking-policy-counter"
                    >
                      {t('settings.policy_notes_counter', { count: bookingPolicy.trim().length })}
                    </p>
                    {bookingPolicyError && (
                      <p
                        id="settings-booking-policy-error"
                        role="alert"
                        style={{
                          color: 'var(--error)',
                          fontFamily: 'var(--font-sans)',
                          fontSize: 'var(--fs-caption)',
                          marginBlockStart: 'var(--sp-2)',
                        }}
                      >
                        {bookingPolicyError}
                      </p>
                    )}
                  </div>
                </>
              )}
              {/* ───────────────────────────────────────────────────────── */}

              {updateMutation.error && (
                <p
                  role="alert"
                  style={{
                    color: 'var(--error)',
                    fontFamily: 'var(--font-sans)',
                    marginBlockEnd: '0.75rem',
                  }}
                >
                  {updateMutation.error.message}
                </p>
              )}

              {success && (
                <p
                  role="status"
                  style={{
                    color: 'var(--success)',
                    fontFamily: 'var(--font-sans)',
                    marginBlockEnd: '0.75rem',
                  }}
                >
                  {t('settings.success')}
                </p>
              )}

              {canEdit && (
                <Button
                  type="submit"
                  variant="primary"
                  size="md"
                  disabled={updateMutation.isPending}
                >
                  {updateMutation.isPending ? t('settings.saving') : t('settings.save')}
                </Button>
              )}
            </form>
          </Card>
        </>
      )}
    </div>
  );
}
